import React, { FC, useEffect, useId, useState } from 'react'

import { FormControlLabel, FormGroup, Box, Grid, Typography, useTheme } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TRADING_TABLE_COLUMNS_ORDER } from '../../constants'
import { updateColumnOrder as updateDailyStatsColumnOrder } from '../../pages/DailyStats'
import { updateColumnOrder as updateTradingLogColumnOrder } from '../../pages/TradingLog'
import { useAppDispatch } from '../../store/hooks'
import { availableTableColumns, getInitialTableColumns } from '../../utils'
import { ControllableCheckbox } from '../ControllableCheckbox'
import { CustomCheckBox } from '../CustomCheckBox'
import { CustomModal } from '../Modal'
import { showToast } from '../Toasts'

import { ColumnSettings, CommonTableColumnsEnum, SelectColumnsModalProps } from './types'

export const SelectColumnsModal: FC<SelectColumnsModalProps> = ({ isOpen, closeModal }) => {
  const { palette, spacing } = useTheme()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [initValues, setInitValues] = useState<ColumnSettings>()

  const tradingLogAvailableColumns = availableTableColumns.tradingLog
  const tradingLogInitialColumns = getInitialTableColumns().tradingLog
  const defaultValues = tradingLogAvailableColumns.reduce((acc, key) => {
    acc[key] = tradingLogInitialColumns.includes(key)
    return acc
  }, {} as { [key in CommonTableColumnsEnum]: boolean })

  const methods = useForm<ColumnSettings>({ defaultValues })
  const { handleSubmit, getValues, setValue, reset } = methods
  const columnsSettingsFormId = useId()

  const formValues = Object.values(getValues())
  const allChecked = formValues.every((value) => value)
  const hasDifferentValues = formValues.some((value) => value !== formValues[0])

  const handleChangeAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    tradingLogAvailableColumns.forEach((column) => setValue(column, event.target.checked))
  }

  const onSubmit = async (data: ColumnSettings) => {
    try {
      const columnsOrder = Object.entries(data)
        .filter(([, value]) => value)
        .map(([key]) => key)
      localStorage.setItem(TRADING_TABLE_COLUMNS_ORDER, JSON.stringify(columnsOrder))
      dispatch(updateTradingLogColumnOrder(columnsOrder))
      dispatch(updateDailyStatsColumnOrder(columnsOrder))
      setInitValues(getValues())
    } catch (error) {
      showToast.error(error.message)
    } finally {
      closeModal()
    }
  }

  const handleCancel = () => {
    reset(initValues)
    closeModal()
  }

  useEffect(() => {
    setInitValues(getValues())
  }, [])

  return (
    <CustomModal
      open={isOpen}
      onClose={handleCancel}
      submitButtonText={t('selectColumnsModal.actions.save', 'Save') as string}
      submitButtonProps={{ form: columnsSettingsFormId, type: 'submit' }}
      title={t('selectColumnsModal.title', 'Select columns')}
    >
      <Box>
        <FormProvider {...methods}>
          <form id={columnsSettingsFormId} onSubmit={handleSubmit(onSubmit)}>
            <Grid
              container
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              padding={spacing(2, 0)}
              borderBottom={`1px solid ${palette.grey[500]}`}
            >
              <Grid item>
                <FormControlLabel
                  label={t('selectColumnsModal.labels.all', 'All')}
                  sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500 } }}
                  control={
                    <CustomCheckBox
                      checked={allChecked}
                      indeterminate={hasDifferentValues}
                      onChange={handleChangeAll}
                    />
                  }
                />
              </Grid>
              <Grid item>
                <Typography
                  color={palette.primary.main}
                  fontWeight={500}
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={() => reset(defaultValues)}
                >
                  {t('selectColumnsModal.actions.reset', 'Reset')}
                </Typography>
              </Grid>
            </Grid>

            <Box
              display="flex"
              gap={4}
              padding={spacing(2, 0)}
              flexWrap="wrap"
              columnGap={{ xs: spacing(2), md: spacing(4) }}
              rowGap={{ xs: spacing(2), md: spacing(4) }}
            >
              <FormGroup sx={{ width: spacing(20) }}>
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.closePrice}
                  label={t('closePrice', 'Close Price')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.closeTime}
                  label={t('closeTime', 'Close Time')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.commissions}
                  label={t('commissions', 'Commissions')}
                />
                <ControllableCheckbox name={CommonTableColumnsEnum.fee} label={t('fee', 'Fee')} />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.swap}
                  label={t('swap', 'Swap')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.grossProfit}
                  label={t('grossProfit', 'Gross Profit (Loss)')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.journalingCompletion}
                  label={t('journaling', 'Journaling')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.netProfit}
                  label={t('netProfit', 'Net Profit (Loss)')}
                />
              </FormGroup>

              <FormGroup sx={{ width: spacing(20) }}>
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.openPrice}
                  label={t('openPrice', 'Open Price')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.openTime}
                  label={t('openTime', 'Open Time')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.pips}
                  label={t('pips', 'Pips')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.riskLevel}
                  label={t('riskLevel', 'Risk Level')}
                />
                <ControllableCheckbox name={CommonTableColumnsEnum.roi} label={t('roi', 'ROI')} />
              </FormGroup>

              <FormGroup sx={{ width: spacing(20) }}>
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.status}
                  label={t('status', 'Status')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.symbol}
                  label={t('symbol', 'Symbol')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.ticket}
                  label={t('ticket', 'Ticket')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.type}
                  label={t('type', 'Type')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.volume}
                  label={t('volume', 'Volume')}
                />
                <ControllableCheckbox
                  name={CommonTableColumnsEnum.lots}
                  label={t('lots', 'Lots')}
                />
              </FormGroup>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </CustomModal>
  )
}
